import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { getScalingPolicy, type ScalingAnchor, type ScalingPolicy } from '@/api/scaling';
import { schedulesIncluded } from '@/lib/schedule';
import { useOrganizationStore } from '@/stores/organization';
import { useSubscriptionStore } from '@/stores/subscription';

export type ScalingState =
  { status: 'loading' } | { status: 'error' } | { status: 'loaded'; policy: ScalingPolicy | null };

const MAX_CONCURRENT_LOADS = 4;
// Policies change on their own (burst runners come and go), so loaded ones are refreshed.
const REFRESH_MS = 60_000;

export function anchorKey(anchor: ScalingAnchor): string {
  return `${anchor.kind}:${anchor.runnerId}`;
}

/**
 * Scaling policies of the dashboard's runners, loaded while the selected organization's plan includes
 * autoscaling (the plans of schedules) and refreshed every minute. Changes made here are applied with `set`.
 */
export function useRunnerScaling(anchors: () => ScalingAnchor[]) {
  const subscription = useSubscriptionStore();
  const organizations = useOrganizationStore();
  const available = computed(
    () =>
      subscription.organizationId !== undefined &&
      subscription.organizationId === organizations.selectedId &&
      schedulesIncluded(subscription.subscription)
  );

  const states = ref<Record<string, ScalingState>>({});
  let queue: ScalingAnchor[] = [];
  let running = 0;
  // Bumped when autoscaling becomes unavailable or the view unmounts, so late responses are dropped.
  let generation = 0;
  let timer: ReturnType<typeof setInterval> | undefined;

  function pump() {
    while (running < MAX_CONCURRENT_LOADS && queue.length > 0) {
      void load(queue.shift()!, generation);
    }
  }

  async function load(anchor: ScalingAnchor, context: number) {
    running++;
    const key = anchorKey(anchor);
    try {
      const policy = await getScalingPolicy(anchor);
      if (context === generation && key in states.value)
        states.value[key] = { status: 'loaded', policy };
    } catch {
      // A refresh keeps what was loaded before.
      if (context === generation && states.value[key]?.status !== 'loaded')
        states.value[key] = { status: 'error' };
    } finally {
      if (context === generation) {
        running--;
        pump();
      }
    }
  }

  function enqueue(anchor: ScalingAnchor, keepState = false) {
    const key = anchorKey(anchor);
    if (!keepState || !(key in states.value)) states.value[key] = { status: 'loading' };
    if (!queue.some((a) => anchorKey(a) === key)) queue.push(anchor);
  }

  watch(
    [available, () => anchors().map(anchorKey).join(',')],
    ([isAvailable]) => {
      if (!isAvailable) {
        generation++;
        running = 0;
        queue = [];
        states.value = {};
        clearInterval(timer);
        timer = undefined;
        return;
      }
      if (timer === undefined) {
        timer = setInterval(() => {
          if (document.hidden) return;
          for (const anchor of anchors())
            if (states.value[anchorKey(anchor)]?.status === 'loaded') enqueue(anchor, true);
          pump();
        }, REFRESH_MS);
      }
      const current = anchors();
      const present = new Set(current.map(anchorKey));
      for (const key of Object.keys(states.value)) if (!present.has(key)) delete states.value[key];
      queue = queue.filter((a) => present.has(anchorKey(a)));
      for (const anchor of current) if (!(anchorKey(anchor) in states.value)) enqueue(anchor);
      pump();
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    generation++;
    clearInterval(timer);
  });

  /** Null when autoscaling is not available, so nothing about it is shown. */
  function stateFor(anchor: ScalingAnchor): ScalingState | null {
    if (!available.value) return null;
    return states.value[anchorKey(anchor)] ?? { status: 'loading' };
  }

  function set(anchor: ScalingAnchor, policy: ScalingPolicy | null) {
    const key = anchorKey(anchor);
    if (key in states.value) states.value[key] = { status: 'loaded', policy };
  }

  function reload(anchor: ScalingAnchor) {
    if (!available.value || !(anchorKey(anchor) in states.value)) return;
    enqueue(anchor);
    pump();
  }

  return { available, stateFor, set, reload };
}
